import { getAssetUrl } from './useAssetUrl'
import { createNadDebug } from '../utils/debugLog'

const mediaFetchLog = createNadDebug('dw:timeline-playback:preload')

export interface MediaUrlOptions {
  width?: number
  cacheKey?: string
}

export interface MediaBlobFetchOptions {
  signal?: AbortSignal
}

/**
 * Build a browser media element URL. Authentication must be embedded in the
 * URL by getAssetUrl() because media elements and credentialless blob fetches
 * cannot rely on Authorization headers.
 */
export function getMediaElementUrl(
  projectId: string,
  assetPath: string | null | undefined,
  options: MediaUrlOptions = {},
): string | null {
  return getAssetUrl(projectId, assetPath, options.width ?? 0, options.cacheKey)
}

export async function fetchMediaBlob(
  projectId: string,
  assetPath: string | null | undefined,
  options: MediaBlobFetchOptions = {},
): Promise<Response> {
  const url = getMediaElementUrl(projectId, assetPath)
  if (!url) {
    throw new Error('Cannot fetch media blob without an asset URL')
  }
  return fetchResolvedMediaBlob(url, options)
}

export async function fetchResolvedMediaBlob(
  url: string,
  options: MediaBlobFetchOptions = {},
): Promise<Response> {
  // Previously this added ``?proxy=true`` to every API-asset URL, which
  // made the API stream bytes through itself instead of redirecting to
  // the GCS signed URL. That was a workaround for the era BEFORE the
  // GCS bucket had a CORS policy — without bucket CORS, the browser
  // would block the cross-origin fetch to GCS. We applied CORS in
  // commit 5ffef0b (scripts/configure-gcs-cors.sh), so the proxy
  // detour is now strictly cost — Cloud Run reading multi-MB videos
  // out of GCS-FUSE and streaming them to the browser when the
  // browser could just hit the CDN directly. For a 9-clip timeline
  // that turned the preview preload into a 30s+ hang.
  //
  // ``proxy=true`` is still a valid query param on the API route for
  // edge cases (e.g. third-party storage without CORS), but it must
  // be opt-in now, not blanket.
  const fetchUrl = url

  mediaFetchLog({
    event: 'media-blob-fetch',
    urlKind: isApiAssetUrl(fetchUrl) ? 'api-asset' : 'absolute',
    hasEmbeddedAuth: hasEmbeddedAuth(fetchUrl),
    credentialsPolicy: 'omit',
    sendsAuthorization: false,
    sendsContentType: false,
  })

  try {
    const response = await fetch(fetchUrl, {
      signal: options.signal,
      credentials: 'omit',
    })

    if ((response.status === 401 || response.status === 403) && isApiAssetUrl(fetchUrl) && !hasEmbeddedAuth(fetchUrl)) {
      throw new Error(
        `Media asset URL is missing embedded auth for credentialless fetch: ${stripQuery(fetchUrl)}`,
      )
    }

    return response
  } catch (error) {
    if (error instanceof Error) {
      throw new Error(`Credentialless media blob fetch failed for ${stripQuery(fetchUrl)}: ${error.message}`)
    }
    throw error
  }
}

function isApiAssetUrl(url: string): boolean {
  try {
    const parsed = new URL(url, getUrlParseBase())
    return /^\/projects\/[^/]+\/assets\//.test(parsed.pathname)
  } catch {
    return false
  }
}

function hasEmbeddedAuth(url: string): boolean {
  try {
    const parsed = new URL(url, getUrlParseBase())
    return parsed.searchParams.has('token') || parsed.searchParams.has('publication_token')
  } catch {
    return false
  }
}

function stripQuery(url: string): string {
  try {
    const parsed = new URL(url, getUrlParseBase())
    parsed.search = ''
    parsed.hash = ''
    return parsed.toString()
  } catch {
    return url.split('?')[0]
  }
}

function getUrlParseBase(): string {
  return typeof window !== 'undefined' ? window.location.origin : 'http://localhost'
}
